import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CustomerDocumentsQuery,
  UpdateCustomerDocumentMutation,
  UpdateCustomerDocumentMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { customerDocumentsKey } from "./useCustomerDocuments";

const UPDATE_CUSTOMER_DOCUMENT_QUERY = graphql(`
  mutation updateCustomerDocument($request: UpdateCustomerDocumentRequest!) {
    updateCustomerDocument(UpdateCustomerDocumentRequest: $request) {
      id
      resourceId
      fileName
      description
      documentType
      uiPath
    }
  }
`);

export function useUpdateCustomerDocument(
  options?: UseMutationOptions<
    UpdateCustomerDocumentMutation,
    unknown,
    UpdateCustomerDocumentMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(UPDATE_CUSTOMER_DOCUMENT_QUERY, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const { id, resourceId, documentType, fileName, description, uiPath } =
        data.updateCustomerDocument;
      options?.onSuccess?.(data, variables, context);
      queryClient.setQueriesData(
        {
          queryKey: customerDocumentsKey({ documentType, resourceId, uiPath }),
          exact: false, // Allow matching without pagination
        },
        (prevData: CustomerDocumentsQuery | undefined) => {
          if (!prevData) return prevData;

          // Update the specific document in the cached data with the newly
          // updated values returned in the query data
          const updatedCustomerDocuments = prevData.customerDocuments.items.map((doc) =>
            doc.id === id ? { ...doc, fileName, description, uiPath } : doc,
          );

          return {
            customerDocuments: {
              items: updatedCustomerDocuments,
              totalCount: prevData.customerDocuments.totalCount,
            },
          };
        },
      );
    },
  });
  return { data: data, ...rest };
}
